import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import { useMemo, useRef, useState } from 'react';
import { Group, Vector3 } from 'three';
import { operationCaptionTargetPoints, placeOperationCaption } from '../_pure/operationCaptionPlacement.js';
export function OperationCaption({ caption, frame, hideInXr = false }) {
    const xrSession = useXR(state => state.session);
    const group = useRef(null);
    const text = useRef(null);
    const background = useRef(null);
    const vector = useMemo(() => new Vector3(), []);
    const [rows, setRows] = useState([caption.text]);
    const words = useMemo(() => rows.flatMap((row, index) => [...row.matchAll(/\S+/g)].map(match => ({
        key: `${index}:${match.index}`, text: match[0], x: (match.index - row.length / 2) * 0.7,
        y: ((rows.length - 1) / 2 - index) * 1.3,
    }))), [rows]);
    const targets = useMemo(() => operationCaptionTargetPoints(frame, caption.targetItemKeys), [caption.targetItemKeys, frame]);
    useFrame(({ camera, size }) => {
        if (!group.current || !text.current || !background.current)
            return;
        if (xrSession && hideInXr) {
            group.current.visible = false;
            return;
        }
        const areas = [{ left: size.width / 2 - 60, right: size.width / 2 + 60,
                top: size.height / 2 - 45, bottom: size.height / 2 + 45 }];
        for (const points of targets) {
            const projected = points.map(point => vector.set(...point).project(camera).clone()).filter(point => point.z < 1);
            if (!projected.length)
                continue;
            areas.push({
                left: Math.min(...projected.map(point => (point.x + 1) * size.width / 2)),
                right: Math.max(...projected.map(point => (point.x + 1) * size.width / 2)),
                top: Math.min(...projected.map(point => (1 - point.y) * size.height / 2)),
                bottom: Math.max(...projected.map(point => (1 - point.y) * size.height / 2)),
            });
        }
        const placement = placeOperationCaption(size.width, size.height, caption.text, areas, caption.layoutText);
        group.current.visible = !!placement;
        if (!placement)
            return;
        const paddedRows = [...placement.rows, ...Array(placement.rowCount - placement.rows.length).fill('')]
            .map(row => row.padEnd(placement.columns));
        if (rows.join('\n') !== paddedRows.join('\n'))
            setRows(paddedRows);
        const x = (placement.left + placement.right) / size.width - 1;
        const y = 1 - (placement.top + placement.bottom) / size.height;
        const center = vector.set(x, y, -0.5).unproject(camera).clone();
        const pixel = vector.set(x + 2 / size.width, y, -0.5).unproject(camera).distanceTo(center);
        group.current.position.copy(center);
        camera.getWorldQuaternion(group.current.quaternion);
        group.current.scale.setScalar(pixel);
        text.current.scale.setScalar(placement.fontPixels);
        background.current.scale.set(placement.right - placement.left, placement.bottom - placement.top, 1);
    });
    return _jsxs("group", { ref: group, renderOrder: 10000, children: [_jsx("group", { ref: background, children: _jsxs("mesh", { position: [0, 0, -0.001], renderOrder: 10000, children: [_jsx("planeGeometry", { args: [1, 1] }), _jsx("meshBasicMaterial", { color: "#102030", transparent: true, opacity: 0.92, depthTest: false, depthWrite: false, toneMapped: false })] }) }), _jsx("group", { ref: text, children: words.map(word => _jsxs(Text, { position: [word.x, word.y, 0], fontSize: 1, anchorX: "left", anchorY: "middle", color: "white", renderOrder: 10001, children: [_jsx("meshBasicMaterial", { depthTest: false, depthWrite: false, toneMapped: false }), word.text] }, word.key)) })] });
}
